import { Button, Typography } from '@mui/material'
import { Box } from '@mui/system'
import BigNumber from 'bignumber.js'
import HorizontalText from 'components/container/HorizontalText'
import { AssetData } from 'constants/assets'
import CoreModal from 'core/CoreModal'
import CoreTokenInput from 'core/CoreTokenInput'
import { useCallback, useMemo, useState } from 'react'
import { toAsset } from 'utils/contractParse'
import RiskRatioBar from 'components/RiskRatioBar'
import { Asset } from 'interfaces/contracts/edgePool'
import { toA } from 'utils/display'
import { observer } from 'mobx-react-lite'
import { oracleStore, poolStore } from 'stores'
import { poolService } from 'services/poolService'
import ValueChange from 'components/container/ValueChange'
import { handleSupplyChange } from 'utils/handleChangeValue'
import { ONE, PERCENT, ZERO } from 'constants/number'
import { buttonStateHelper } from 'utils/buttonStateHelper'
import styled from '@emotion/styled'
import { useModal } from 'react-modal-hook'
import BorrowWithHeighRiskModal from './BorrowWithHeighRiskModal'
import { ForceMaxRatio, SafeLTVRatio } from 'constants/constants'
import { theme } from 'theme'
import CoreSwitch from 'core/CoreSwitch'
import ModalTextSpacing from 'components/container/ModalTextSpacing'
import useSafeRatioHelper from 'hooks/useSafeRatioHelper'

const CircleNumber = styled(Box)`
  background-color: ${theme.palette.primary.main};
  border-radius: 100%;
  height: 24px !important;
  width: 24px !important;
  text-align: center;
  align-content: center;
  font-size: 14;
  font-weight: 700;
  color: white;
`

interface Props {
  onClose: () => void
  sendBorrow: (asset: Asset) => void
  assetData: AssetData
}

const BorrowModal = observer(({ onClose, sendBorrow, assetData }: Props) => {
  const poolAddr = poolService.poolAddress
  const {
    borrowLimit,
    walletAllBorrowBalUSD,
    borrowAPY,
    walletNetAPY,
    walletBorrowingPowerUSD: borrowingPowerUSD,
    walletCollateralValueUSD,
    walletTotalDebtValueUSD,
    marketInfo,
  } = poolStore.getMarketOverall(poolAddr, assetData.terraAddress)
  const { borrow_factor } = marketInfo[assetData.terraAddress].config
  const assetPrice = oracleStore.getAssetPrice(poolAddr, assetData.terraAddress)
  const { safeRatioUSD: _safeRatioUSD, forceMaxUSD } = useSafeRatioHelper({
    walletTotalDebtValueUSD,
    walletCollateralValueUSD,
    mode: 'borrow',
  })
  const safeRatioUSD = _safeRatioUSD.div(assetPrice).times(borrow_factor)
  const [amount, setAmount] = useState<string>('')

  const [afAllBorrowUSD, afNetAPY, afBorrowingPower] = useMemo(() => {
    if (!assetData) return [undefined, undefined]
    const amountUSD = (amount === '' ? ZERO : new BigNumber(amount))
      .times(assetPrice)
      .div(borrow_factor)
    const [_borrow, _netAPY] = handleSupplyChange({
      supply: walletAllBorrowBalUSD,
      change: amountUSD.div(borrow_factor),
      netAPY: walletNetAPY,
      supplyAPY: borrowAPY.times(-1),
    })
    const s = _borrow && `$${toA(_borrow)}`
    const n = _netAPY && `${toA(_netAPY)}%`
    const b =
      _borrow &&
      borrowingPowerUSD &&
      `$${borrowingPowerUSD.minus(amountUSD).toFixed(2)}`
    return [s, n, b]
  }, [
    assetData,
    amount,
    assetPrice,
    walletAllBorrowBalUSD,
    borrow_factor,
    walletNetAPY,
    borrowAPY,
    borrowingPowerUSD,
  ])

  const calculateLTV = useCallback(
    (_amount: string) => {
      const amountUSD = (_amount === '' ? ZERO : new BigNumber(_amount))
        .times(assetPrice)
        .div(borrow_factor)

      if (!walletTotalDebtValueUSD) return ZERO
      if (walletCollateralValueUSD?.eq(ZERO)) return ZERO
      return walletTotalDebtValueUSD
        .plus(amountUSD)
        .div(walletCollateralValueUSD || ONE)
        .times(PERCENT)
    },
    [
      assetPrice,
      borrow_factor,
      walletCollateralValueUSD,
      walletTotalDebtValueUSD,
    ],
  )

  const newLTV = useMemo(() => {
    return calculateLTV(amount)
  }, [calculateLTV, amount])

  const handleOnAmountChange = (value: string) => {
    const _newLTV = calculateLTV(value)
    if (_newLTV.gt(ForceMaxRatio)) {
      setAmount(forceMaxUSD.div(assetPrice).times(borrow_factor).toString())
    } else {
      setAmount(value)
    }
  }

  const handleOnSend = useCallback(() => {
    sendBorrow(
      toAsset(
        assetData?.terraAddress || '',
        new BigNumber(amount).times(new BigNumber(`1e${assetData.decimals}`)),
      ),
    )
    onClose()
  }, [amount, assetData.decimals, assetData?.terraAddress, onClose, sendBorrow])

  const [showHighRisk, hideHighRisk] = useModal(
    () => (
      <BorrowWithHeighRiskModal
        onClose={hideHighRisk}
        onProcess={() => {
          hideHighRisk()
          handleOnSend()
        }}
      />
    ),
    [handleOnSend],
  )

  const inWalletUSD = borrowLimit.times(assetPrice)
  const { state: btnState, disabled } = buttonStateHelper({
    forceDisable: newLTV.gt(ForceMaxRatio),
    amount,
    max: borrowLimit,
    needApprove: false,
    state: {
      zeroMax: `No ${assetData.symbol} available to borrow`,
      overflowAmount: `Exceed borrow limit`,
      actual: 'Borrow',
    },
  })

  const Content = (
    <Box sx={{ textAlign: 'left' }}>
      <Typography variant="h5">Borrow</Typography>
      <br />
      {assetData && (
        <CoreTokenInput
          value={amount}
          assetDatas={[assetData]}
          onChange={handleOnAmountChange}
          maxValue={borrowLimit}
          safeValue={safeRatioUSD}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          margin: '32px 0px 16px 0px',
        }}
      >
        <HorizontalText
          left="Borrow limit"
          right={`${toA(borrowLimit)} ${assetData?.symbol} ($${toA(
            inWalletUSD,
          )})`}
        />
        <ModalTextSpacing
          itl={<Typography variant="body2">Net APY</Typography>}
          itr={<ValueChange bf={`${toA(walletNetAPY)}%`} af={afNetAPY} />}
        />
        <ModalTextSpacing
          itl={<Typography variant="body2">Borrowing power left</Typography>}
          itr={
            <ValueChange
              bf={`$${borrowingPowerUSD?.toFixed(2)}`}
              af={afBorrowingPower}
            />
          }
        />
      </Box>
      <RiskRatioBar
        RiskRatio={newLTV}
        title="Risk ratio"
        sx={{ marginBottom: '32px' }}
        showFullText
      />
      <Button
        variant="contained"
        fullWidth
        onClick={newLTV.gt(SafeLTVRatio) ? showHighRisk : handleOnSend}
        disabled={disabled}
      >
        {btnState}
      </Button>
    </Box>
  )

  const ContentZeroBorrowingPower = (
    <Box sx={{ textAlign: 'left' }}>
      <Typography variant="h5" mb="24px">
        Borrow
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          marginBottom: '30px',
        }}
      >
        <Typography paragraph variant="body1" fontWeight={600}>
          Make sure you have enough borrowing power to start with. To boost your
          borrowing power,
        </Typography>
        <Box display="flex" gap="14px">
          <CircleNumber>1</CircleNumber>
          <Typography variant="body2">
            In the supply market, supply an asset.
          </Typography>
        </Box>
        <Box display="flex" gap="14px">
          <CircleNumber>2</CircleNumber>
          <Box>
            <Typography variant="body2">
              Turn the toggle of that asset from
            </Typography>
            <Typography variant="body2">
              in order to use it as collateral.
            </Typography>
          </Box>
          <CoreSwitch checked={false} />
          <Typography variant="body2">to</Typography>
          <CoreSwitch checked={true} />
        </Box>
        <hr />
        <ModalTextSpacing
          itl={<Typography variant="body2">Borrowing power left</Typography>}
          itr={
            <ValueChange
              bf={`$${borrowingPowerUSD?.toFixed(2)}`}
              af={afBorrowingPower}
            />
          }
        />
      </Box>
      <Button variant="contained" fullWidth disabled>
        Not enough borrowing power
      </Button>
    </Box>
  )

  return (
    <CoreModal
      open
      content={
        borrowingPowerUSD?.lte(ZERO) ? ContentZeroBorrowingPower : Content
      }
      onClose={onClose}
    />
  )
})

export default BorrowModal
